/*
  ALLMEAT PDP helpers.

  1. <am-gallery> — the mockup's "big image + thumbnail strip" gallery. It exposes
     setActiveMedia(mediaId) because Dawn's VariantSelects.updateMedia() calls that
     method on #MediaGallery-<sectionId> whenever the chosen variant has its own image.
  2. Keeps the price shown inside the add-to-cart button in sync with the variant,
     since Dawn only re-renders the #price-<sectionId> block.
*/
(function () {
  class AmGallery extends HTMLElement {
    constructor() {
      super();
      this.mainImage = this.querySelector('[data-am-gallery-main] img');
      this.thumbs = Array.from(this.querySelectorAll('[data-am-thumb]'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.activate(thumb));
      });
    }

    activate(thumb) {
      if (!thumb) return;
      if (this.mainImage) {
        const full = thumb.dataset.full;
        const source = thumb.querySelector('img');
        if (full) this.mainImage.src = full;
        if (source) this.mainImage.alt = source.alt;
        this.mainImage.dataset.mediaId = thumb.dataset.mediaId;
      }
      this.thumbs.forEach((other) => other.classList.toggle('on', other === thumb));
    }

    // Called by Dawn's variant picker.
    setActiveMedia(mediaId) {
      this.activate(this.thumbs.find((thumb) => thumb.dataset.mediaId === mediaId));
    }
  }

  if (!customElements.get('am-gallery')) customElements.define('am-gallery', AmGallery);

  function syncBuyPrice(root) {
    const priceBlock = root.querySelector('.pdp-price[id^="price-"]');
    const buyPrice = root.querySelector('[data-am-buy-price]');
    if (!priceBlock || !buyPrice || priceBlock.dataset.amPriceSync === 'true') return;
    priceBlock.dataset.amPriceSync = 'true';

    // Dawn replaces the whole price block asynchronously after a variant change,
    // so watch the block instead of listening for the (earlier) input event.
    new MutationObserver(function () {
      const now = priceBlock.querySelector('.now');
      if (now) buyPrice.textContent = now.textContent.trim();
    }).observe(priceBlock, { childList: true, subtree: true, characterData: true });
  }

  /*
    The Order Delivery Date app injects its calendar wherever it likes — on the
    cart that lands after the two-column shell, which is not where the mockup
    puts the picker, so the widget has to be adopted into the reserved slot.

    It must be adopted by its REACT ROOT, not by the calendar itself. React 17+
    delegates every synthetic event to the root container, so lifting a child
    (`.identixweb-order-delivery-date-main`) out of that container leaves a
    widget that renders perfectly and answers no clicks — the T12 bug: on the
    draft theme a day click never set `rmdp-selected`, the same click worked on
    live. Moving the container itself takes the listeners with it.
  */
  const DELIVERY_WIDGET = '.identixweb-order-delivery-date-main';

  // React tags its root container with `__reactContainer$<n>`; children only
  // carry `__reactFiber$`/`__reactProps$`. That is the element we may move.
  function reactRootOf(node) {
    for (let el = node; el && el !== document.body; el = el.parentElement) {
      if (Object.keys(el).some((key) => key.indexOf('__reactContainer$') === 0)) return el;
    }
    return null;
  }

  function watchDeliverySlot(slot) {
    if (slot.dataset.amSlotWatched === 'true') return;
    slot.dataset.amSlotWatched = 'true';

    const target = slot.querySelector('.am-odd-target') || slot;
    const sync = function () {
      const widget = document.querySelector(DELIVERY_WIDGET);
      if (widget && !target.contains(widget)) {
        // No React root found (app rewritten, plain DOM widget) → leave it where
        // it is rather than break it; the slot simply stays collapsed.
        const root = reactRootOf(widget);
        if (root && !target.contains(root)) target.appendChild(root);
      }
      slot.classList.toggle('am-filled', target.children.length > 0);
    };
    sync();
    new MutationObserver(sync).observe(document.body, { childList: true, subtree: true });
  }

  /*
    Shopify overwrites a cart attribute with whatever `attributes[…]` field the
    cart form posts, so an EMPTY field deletes the attribute. The Order Delivery
    Date app ships its hidden fields blank until a date is picked, and once its
    widget lives inside `form#cart` those blanks are posted on checkout and wipe
    a delivery date that was set anywhere else. Drop the blank ones from the
    submission — never the filled ones.
  */
  function guardEmptyCartAttributes() {
    if (document.documentElement.dataset.amAttrGuard === 'true') return;
    document.documentElement.dataset.amAttrGuard = 'true';

    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if ((form.getAttribute('action') || '').indexOf('/cart') === -1) return;

        const blanks = Array.from(form.querySelectorAll('input[name^="attributes["]')).filter(
          (input) => !input.disabled && input.value.trim() === ''
        );
        blanks.forEach((input) => {
          input.disabled = true;
        });
        // Dawn also posts the cart over fetch; put them back once this submit is gone.
        setTimeout(function () {
          blanks.forEach((input) => {
            input.disabled = false;
          });
        }, 0);
      },
      true
    );
  }

  function readPumperList(id) {
    const node = document.getElementById(id);
    if (!node) return [];
    return node.textContent
      .replace(/\s/g, '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function readPumperVariantMap() {
    const node = document.getElementById('prvw_variant_map');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function currentPumperVariantCents() {
    const variantMap = readPumperVariantMap();
    if (!variantMap) return null;

    const selectedId =
      document.querySelector('product-form.am-buy-form [name="id"]')?.value ||
      document.getElementById('prvw_default_variant_id')?.value ||
      '';
    const variants = Object.values(variantMap);
    const selected = variants.find((variant) => String(variant.id) === String(selectedId)) || variants[0];
    const cents = Number(selected?.price);

    return Number.isFinite(cents) ? cents : null;
  }

  function pumperMoneyParts(format) {
    if (format.indexOf('amount_with_comma_separator') !== -1) {
      return { placeholder: 'amount_with_comma_separator', thousands: '.', decimal: ',' };
    }
    if (format.indexOf('amount_no_decimals_with_comma_separator') !== -1) {
      return { placeholder: 'amount_no_decimals_with_comma_separator', thousands: '.', decimal: ',', precision: 0 };
    }
    if (format.indexOf('amount_no_decimals') !== -1) {
      return { placeholder: 'amount_no_decimals', thousands: ',', decimal: '.', precision: 0 };
    }
    if (format.indexOf('amount_with_apostrophe_separator') !== -1) {
      return { placeholder: 'amount_with_apostrophe_separator', thousands: "'", decimal: '.' };
    }
    return { placeholder: 'amount', thousands: ',', decimal: '.' };
  }

  function formatPumperMoney(cents) {
    const format =
      document.getElementById('prvw_money_format')?.value ||
      window.money_format ||
      window.Shopify?.money_format ||
      '€{{amount_with_comma_separator}}';
    const parts = pumperMoneyParts(format);
    const precision = parts.precision ?? 2;
    const amount = (Math.max(0, cents) / 100).toFixed(precision);
    const split = amount.split('.');
    const whole = split[0].replace(/\B(?=(\d{3})+(?!\d))/g, parts.thousands);
    const decimal = precision > 0 ? parts.decimal + split[1] : '';

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, whole + decimal);
  }

  function pumperCartFinalCents(unitCents, quantity, discountType, discountValue) {
    const qty = Math.max(1, Number.parseInt(quantity, 10) || 1);
    const discount = Number(discountValue) || 0;
    const original = unitCents * qty;

    if (discountType === 'percentage') {
      return Math.max(0, (unitCents - Math.floor((unitCents * discount) / 100)) * qty);
    }
    if (discountType === 'amount') {
      return Math.max(0, original - Math.round(discount * 100));
    }
    if (discountType === 'each') {
      return Math.max(0, (unitCents - Math.round(discount * 100)) * qty);
    }
    if (discountType === 'fixed') {
      return Math.max(0, Math.round(discount * 100));
    }
    return original;
  }

  function selectedPumperTierIndex() {
    const selected = document.querySelector('#prvw__bundle_container input[name="cb"]:checked');
    if (selected) return Math.max(0, Number.parseInt(selected.value, 10) - 1);

    const fallback = document.getElementById('prvw_default_selected')?.value;
    return Math.max(0, (Number.parseInt(fallback, 10) || 1) - 1);
  }

  function syncPumperTierTotals() {
    const root = document.getElementById('prvw__bundle_container');
    if (!root) return;

    const unitCents = currentPumperVariantCents();
    const quantities = readPumperList('prvw_dynamic_quantity');
    const discountTypes = readPumperList('prvw_dynamic_price');
    const discounts = readPumperList('prvw_dynamic_discount');
    if (!unitCents || !quantities.length || !discountTypes.length) return;

    const totals = quantities.map((quantity, index) => {
      const finalCents = pumperCartFinalCents(unitCents, quantity, discountTypes[index], discounts[index]);
      const text = formatPumperMoney(finalCents);

      root.querySelectorAll(`.prvw_totalAmount_price_${index}`).forEach((price) => {
        if (price.textContent.trim() !== text) price.textContent = text;
      });

      return { finalCents, text };
    });

    const selectedIndex = selectedPumperTierIndex();
    const selectedTotal = totals[selectedIndex];
    const bottom = document.getElementById('bottomtitle__total__price');
    if (bottom && selectedTotal && bottom.textContent.trim() !== selectedTotal.text) {
      bottom.textContent = selectedTotal.text;
    }

  }

  function watchPumperTierTotals() {
    if (document.documentElement.dataset.amPumperRounding === 'true') return;
    document.documentElement.dataset.amPumperRounding = 'true';

    let syncFrame = null;
    const schedule = function () {
      if (syncFrame) window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(function () {
        syncFrame = null;
        syncPumperTierTotals();
      });
    };

    document.addEventListener('change', schedule, true);
    document.addEventListener('click', schedule, true);
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
    schedule();

    window.amSyncPumperTierTotals = syncPumperTierTotals;
  }

  function init() {
    document.querySelectorAll('.am-product-section').forEach(syncBuyPrice);
    document.querySelectorAll('[data-am-delivery-slot]').forEach(watchDeliverySlot);
    guardEmptyCartAttributes();
    watchPumperTierTotals();
  }

  /*
    T29 — the quick-add modal showed the default variant's price on the button
    (€10) after the shopper picked €50/€100. `.am-product-section` is a class on
    the `<section id="MainProduct-…">` ELEMENT, and quick-add.js copies only that
    section's `innerHTML` — so the class never reaches the modal and `init()`'s
    selector matched nothing there, leaving the injected price block without the
    MutationObserver that copies the live price onto the button. (Measured:
    work/t28/logs/bug1.json — variant id and Dawn's own price block both updated
    correctly, only `[data-am-buy-price]` stayed stale.) quick-add.js now marks
    its modal content as a product section and calls this; every helper here is
    guarded by a dataset flag, so re-entry is a no-op.
  */
  window.amProductInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
